import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import he from 'he';
import axiosPrivate from 'config/axiosPrivate';

import '../admin/textDisplay.css';

// Redux Store
import {
  adminClientRedactionDataSelector,
  clearAdminClientRedactionData,
  setCurrentRedaction,
} from 'store/AdminClient/RedactionData';
import {
  fetchAdminClientTextOrderDisplay,
  fetchadminCLientOrderDisplay,
} from 'store/AdminClient/OrderDisplay/features';
import { adminCLientOrderDisplaySelector } from 'store/AdminClient/OrderDisplay';
import { UserSessionSelector } from 'store/UserSession';

// Context Providers
import { WordsProvider } from 'utils/contexts/ScoreBoldWordsContext';
import { SemanticScoreProvider } from 'utils/contexts/SemanticScoreContext';

// Components
import { Form } from '@unform/web';
import AnalyseText from 'components/Admin/AnalyseText';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import KeywordStuffingModal from 'components/shared/modal/KeywordStuffingModals/KeywordStuffingModal';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import { LanguageDropdown } from 'components/shared/TextDisplay/LanguageDropdown';
import { WordCountDisplay } from 'components/shared/TextDisplay/WordCountDisplay';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { RevisionDetails } from 'components/shared/RevisionDetails';

// Helpers
import { checkDataAdmin } from 'helpers/formHelper';
import {
  extractStrKeywordsWithFrequency,
  transformToSemanticGap,
  extractKeywordsWithFrequency,
  formatKeywordData,
} from 'helpers/FormatKeywordsHelper';
import { autoSaveText } from 'helpers/CreateAutoSave';


import {
  ROLES,
  GOVERNED_WRITERS_WITH_VALIDATION,
  KeywordStuffingModalPrompts,
} from 'utils/Constants';
import { getScoringLanguageLabel } from 'helpers/ProjectLanguageHelper';
import { extractWords } from 'utils/keywordStuffing/keywordStuffing';
import { keywordsAutoGeneration } from 'utils/KeywordsAutoGeneration/keywordsAutoGeneration';

import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';

import { useAdminClientTextForm } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextForm';
import { useAdminClientTextSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextSave';
import { useAdminClientDeliverText } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientDeliverText';
import { useAdminClientKeywordStuffing } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientKeywordStuffing';
import { useAdminClientFormValidation } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientFormValidation';
import { useAdminClientPutToDraft } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientPutToDraft';
import { useAdminClientAutoSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientAutoSave';
import usePageTracking from 'utils/hooks/usePageTracking';

import { replaceTextInBold, entitiesToSpecialChars } from 'utils/StringUtils';

const WRITER_CLIENT_TEXT_DETAILS_PAGE = '/WriterClient/TextDetails';
const wordsInBold = [];
const semanticScore = 0;

function WriterClientTextDisplayContent() {
  const RED = '#EE3900';
  const ORANGE = '#FF8D4B';
  const BLACK = '#3c3c3c';

  const [analyseCount, setAnalyseCount] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSendRevisionDisabled, setIsSendRevisionDisabled] = useState(false);
  const [isDraftDisabled, setIsDraftDisabled] = useState(true);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');

  const [showComparison, setShowComparison] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const [color, setColor] = useState(BLACK);
  const [allWebsitesArePresent, setAllWebsitesArePresent] = useState(false);
  const [autoKeywordsGenerationLoading, setAutokeywordGenerationLoading] =
    useState(false);
  const [validTextNbr, setValidTextNbr] = useState(false);
  const [isAutomaticTextAnalysis, setIsAutomaticTextAnalysis] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [hasPerformedInitialSave, setHasPerformedInitialSave] = useState(false);
  const [shouldReloadAfterSave, setShouldReloadAfterSave] = useState(false);
  const [savedTextId, setSavedTextId] = useState(null);

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const { currentRedaction, isLoading, isError } = useSelector(
    adminClientRedactionDataSelector
  );
  const { order } = useSelector(adminCLientOrderDisplaySelector);




  const redactionData = currentRedaction;

  const projectLanguage = useMemo(
    () =>
      getScoringLanguageLabel(
        order?.website?.language || order?.language
      ),
    [order?.website?.language, order?.language]
  );

  useEffect(() => {
    const projectId = redactionData?.projectId || redactionData?.orderId;
    if (!projectId) return;
    if (String(order?.id) === String(projectId)) return;
    dispatch(fetchadminCLientOrderDisplay(projectId));
  }, [redactionData?.projectId, redactionData?.orderId, order?.id, dispatch]);

  const [pendingTextType, setPendingTextType] = useState(() => {
    try {
      return window.localStorage.getItem('WriterClient_PendingTextType') || '';
    } catch {
      return '';
    }
  });

  const normalizedPendingTextType = (pendingTextType || '').toUpperCase();

  const normalizedOrderTextType = (
    order?.textType ||
    normalizedPendingTextType ||
    ''
  ).toUpperCase();

  const normalizedRedactionTextType = (
    redactionData?.type ||
    redactionData?.textType ||
    normalizedPendingTextType ||
    redactionData?.frontendType ||
    ''
  ).toUpperCase();
  const isTranslationText =
    normalizedOrderTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATED';
  const uiTextType = isTranslationText ? 'TRANSLATION' : 'EDITORIAL';
  const backendTextType =
    redactionData?.type ||
    (isTranslationText ? 'TRANSLATED' : 'NON_TRANSLATED');

  const [currentContentScore, setCurrentContentScore] = useState(null);

  const formRef = useRef(null);
  const isInitialized = useRef(false);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
    handleScrollBottom,
  } = useSidebar(uiTextType);

  const shouldDisplaySidebar = showSidebar && !isTranslationText;

  useEffect(() => {
    if (normalizedRedactionTextType) {
      try {
        window.localStorage.removeItem('WriterClient_PendingTextType');
      } catch { }
      if (pendingTextType) {
        setPendingTextType('');
      }
    }
  }, [normalizedRedactionTextType, pendingTextType]);

  useEffect(() => {
    setHasPerformedInitialSave(false);
  }, [redactionData?.id]);

  console.log('WriterClient sidebar debug', {
    normalizedOrderTextType,
    normalizedRedactionTextType,
    isTranslationText,
    showSidebar,
    shouldDisplaySidebar,
  });


  const {
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
    list,
    stuffedKeywords,
    websites,
    initialTextContent,
    initialHtmlContent,
    // Setters
    setTextTitle,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setKeywords,
    setCombinedKeywords,
    setCombinedKeywordsList,
    setList,
    setStuffedKeywords,
    setWebsites,
    setInitialTextContent,
    setInitialHtmlContent,
  } = useAdminClientTextForm(redactionData, 'SMART', isLoading);

  const isTextSaved = useMemo(() => {
    return (
      hasPerformedInitialSave ||
      Boolean(
        actualHtmlContent?.trim()?.length ||
        actualTextContent?.trim()?.length ||
        redactionData?.actualHtmlContent?.trim()?.length ||
        redactionData?.actualTextContent?.trim()?.length
      )
    );
  }, [
    hasPerformedInitialSave,
    actualHtmlContent,
    actualTextContent,
    redactionData?.actualHtmlContent,
    redactionData?.actualTextContent,
  ]);

  const {
    editorRef,
    stepOneEditorRef,
    editorInitialized,
    stepOneEditorInitialized,
    handleEditorInit,
    handleStepOneEditorInit,
  } = useEditorManagement();

  const {
    wordCount: initialTextWordCount,
    setWordCount: setInitialTextWordCount,
    getWordCount,
  } = useWordCountTracker(stepOneEditorRef, stepOneEditorInitialized);

  const {
    wordCount: actualTextWordCount,
    debouncedSetWordCount: debouncedSetActualTextWordCount,
  } = useWordCountTracker(editorRef, editorInitialized);


  const computedInitialWordCount = useMemo(() => {
    if (stepOneEditorRef.current && stepOneEditorInitialized) {
      try {
        const count = stepOneEditorRef.current.plugins?.wordcount?.getCount();
        if (count !== undefined && count !== null) {
          return count;
        }
      } catch (e) {
      }
    }

    // Sinon, calculer depuis le contenu texte
    if (initialTextContent) {
      const text = new DOMParser()
        .parseFromString(initialTextContent, 'text/html')
        .documentElement.textContent || '';
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      return words.length;
    }

    return initialTextWordCount || 0;
  }, [initialTextContent, stepOneEditorInitialized, initialTextWordCount]);


  const {
    isPremium: hasInitialSection,
    hasInitialTextContent,
    isNotTranslation: isEditorial,
    isDelivered,
    isRevisionDone,
    isRevisionSent,
    isDraft,
    isEmpty,
  } = useTextStatus(redactionData, 'SMART', user.role, uiTextType);

  const { deliverText, isTextDelivered, setIsTextDelivered } =
    useAdminClientDeliverText({
      t,
      textType: uiTextType,
      combinedKeywords,
      setErrorSuccessMsg,
      setIsSuccess,
      setErrorSuccess,
      titleTag,
      metaDescriptionTag,
      setIsFetchLoading,
      actualHtmlContent,
      actualTextContent,
      semanticScore,
      initialHtmlContent,
      initialTextContent,
      keyword,
      textTitle,
      actualTextWordCount,
      initialTextWordCount,
      url,
      websites,
      setErrorSuccessImg,
      redactionId: redactionData?.id,
      formData: redactionData,
      currentContentScore: currentContentScore,
    });

  const {
    keywordStuffingLoading,
    initialStuffedKeywords,
    showKeywordStuffingModal,
    keywordStuffingModalTitle,
    keywordStuffingModalMsg,
    keywordStuffingModalData,
    keywordStuffingModalCloseBtn,
    keywordStuffingCall,
    setShowKeywordStuffingModal,
    setInitialStuffedKeywords,
  } = useAdminClientKeywordStuffing({
    setSidebarCollapsed,
    handleScrollBottom,
    actualHtmlContent,
    setActualHtmlContent,
    textTitle,
    keywords,
    extractWords,
    textType: uiTextType,
    initialTextContent,
    actualTextContent,
    initialTextWordCount,
    actualTextWordCount,
    setStuffedKeywords,
    deliverText,
    t,
    setActualTextContent,
    setInitialHtmlContent,
  });

  const { handleSave, isTextFieldDisabled } = useAdminClientTextSave({
    redactionData: redactionData,
    initialHtmlContent,
    initialTextContent,
    actualTextContent,
    actualHtmlContent,
    actualTextWordCount,
    initialTextWordCount,
    combinedKeywords,
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    url,
    textType: backendTextType,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    analyseResult,
    redactionId: redactionData?.id,
    setConfirmation,
    setActualTextContent,
    setActualHtmlContent,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
    t,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    aiAgentModel: redactionData?.aiAgentModel || '',
    projectId: redactionData?.projectId || redactionData?.orderId || order?.id || null,
    currentContentScore: currentContentScore,
    currentSemanticGap: list,
  });

  const { putToDraft } = useAdminClientPutToDraft({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    list,
    combinedKeywords,
    websites,
    stuffedKeywords,
    textTitle,
    titleTag,
    url,
    textId: redactionData?.id,
    projectId: order?.id || redactionData?.orderId,
    initialTextContent,
    initialHtmlContent,
    initialStuffedKeywords,
    possibleStuffedKeywords: [],
    actualTextWordCount,
    initialTextWordCount,
    type: backendTextType,
    activeInitialText: redactionData?.activeInitialText,
    totalNumberOfTextAdded: 0,
    contentScore: currentContentScore || redactionData?.contentScore || null,
    writerProjectId: redactionData?.writerProjectId || 0,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    currentSemanticGap: list,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { autoSave } = useAdminClientAutoSave({
    textId: redactionData?.id,
  });

  const { scheduleSave, isSaving } = useAutosave(autoSave, 30000);
  const lastContentScoreRef = useRef(null);

  const handleScoreDataChange = useCallback(
    async (scoreData) => {
      setCurrentContentScore(scoreData);

      const previousScore = lastContentScoreRef.current;
      lastContentScoreRef.current = scoreData;

      const prevGlobal = previousScore?.globalScore ?? null;
      const nextGlobal = scoreData?.globalScore ?? null;

      if (!redactionData?.id || nextGlobal == null) {
        return;
      }

      if (prevGlobal !== null && prevGlobal === nextGlobal) {
        return;
      }

      try {
        await autoSave(
          actualTextContent || '',
          actualHtmlContent || '',
          redactionData,
          initialHtmlContent,
          initialTextContent,
          'SMART',
          url,
          titleTag,
          metaDescriptionTag,
          scoreData
        );
      } catch (e) {
        console.error('❌ Auto-save après mise à jour du contentScore (WriterClient) a échoué:', e);
      }
    },
    [
      autoSave,
      actualTextContent,
      actualHtmlContent,
      redactionData,
      initialHtmlContent,
      initialTextContent,
      url,
      titleTag,
      metaDescriptionTag,
    ]
  );

  const { checkValidForm, disabledForm } = useAdminClientFormValidation({
    actualTextContent,
    actualTextWordCount,
    analyseResult,
    formData: redactionData,
    initialTextWordCount,
    keyword,
    metaDescriptionTag,
    minWords: 0,
    textTitle,
    textType: uiTextType,
    titleTag,
    validTextNbr,
    semanticScore,
  });

  const enableSave = useCallback(() => {
    if (!redactionData || isEmpty(initialTextContent) || isEmpty(url)) {
      setIsSaveDisabled(true);
      return;
    }
    setIsSaveDisabled(false);
  }, [redactionData, initialTextContent, url]);

  const reloadRedactionData = useCallback(async (textId) => {
    if (!textId) return;

    try {
      setIsFetchLoading(true);
      const res = await axiosPrivate.get(`/texts/external/${textId}/fullDetails`);
      const d = res?.data || {};

      dispatch(
        setCurrentRedaction({
          id: d.id || textId,
          title: d.textTitle || d.keyword || '',
          textTitle: d.textTitle || d.keyword || '',
          keyword: d.keyword || '',
          url: d.url || '',
          titleTag: d.titleTag || '',
          metaDescriptionTag: d.metaDescriptionTag || '',
          initialHtmlContent: d.initialHtmlContent || '',
          initialTextContent: d.initialTextContent || '',
          actualHtmlContent: d.actualHtmlContent || '',
          actualTextContent: d.actualTextContent || '',
          semanticGap: d.semanticGap || [],
          analyseResult: d.analyseResult || null,
          stuffedKeywords: d.stuffedKeywords || [],
          internalLinking: d.internalLinking || '',
          delivered: !!d.delivered,
          revisionSent: !!d.revisionSent,
          revisionDone: !!d.revisionDone,
          globalRevisionSent: !!d.globalRevisionSent,
          validated: !!d.validated,
          orderId: d.projectId || redactionData?.orderId || order?.id,
          createdAt: d.createdAt || redactionData?.createdAt || new Date().toISOString(),
          initialScoreData: d.initialScoreData || null,
          type: d.type || redactionData?.type || '',
          textType: d.type || redactionData?.textType || '',
          frontendType: d.type || redactionData?.frontendType || '',
          websiteId: d.websiteId || redactionData?.websiteId || order?.websiteId || 0,
          aiAgentId: d.aiAgentId || redactionData?.aiAgentId || '',
          aiAgentModel: d.aiAgentModel || redactionData?.aiAgentModel || '',
          projectId: d.projectId || redactionData?.projectId || redactionData?.orderId || order?.id,
          contentScore: d.contentScore || redactionData?.contentScore || null,
          writerProjectId: d.writerProjectId || redactionData?.writerProjectId || 0,
          activeInitialText: d.activeInitialText !== undefined ? d.activeInitialText : (redactionData?.activeInitialText !== undefined ? redactionData.activeInitialText : true),
        })
      );
    } catch (error) {
      console.error('❌ Erreur lors du rechargement des données:', error);
    } finally {
      setIsFetchLoading(false);
    }
  }, [dispatch, redactionData, order]);

  function handleKeywordChange(e) {
    const newValue = e.target.value;
    setCombinedKeywords(newValue);
    setKeywords(extractStrKeywordsWithFrequency(newValue));

    const newGap = transformToSemanticGap(newValue);
    setList(newGap);
  }

  function handleGapUpdate(newGap) {
    setList(newGap);
    setKeywords(extractKeywordsWithFrequency(newGap));
    setCombinedKeywords(formatKeywordData(newGap));
  }

  const enableSendRevision = useCallback(() => {
    if (redactionData && redactionData.revisionDone) {
      setIsSendRevisionDisabled(true);
      return;
    }
    setIsSendRevisionDisabled(false);
  }, [redactionData]);

  const enableDraft = useCallback(() => {

    const hasContent = actualTextContent && actualTextContent.trim().length > 0;
    setIsDraftDisabled(!hasContent);
  }, [actualTextContent]);

  async function validateData(data, { reset }, event) {
    let action = event.nativeEvent.submitter.value;
    console.log('🔍 Action du bouton:', action);

    let finalHtmlContent = actualHtmlContent;

    if (action === 'PutToDraft' || action === 'DeliverText') {

      finalHtmlContent = await applyHighlightingAndWait();
    }

    const wrappedPutToDraft = (demande) => {

      return putToDraft(demande, finalHtmlContent);
    };
    const wrappedDeliverText = (demande) =>
      deliverText(demande, finalHtmlContent);

    console.log('🔍 Appel à checkDataAdmin avec action:', action);
    await checkDataAdmin(
      data,
      'SMART', // orderType
      formRef,
      action,
      wrappedPutToDraft,
      wrappedDeliverText
    );
  }

  function resetFields() {
    setTextTitle('');
    setKeyword('');
    setUrl('');
    setTitleTag('');
    setMetaDescriptionTag('');
    setActualTextContent('');
    setActualHtmlContent('');
    setInitialTextContent('');
    setInitialHtmlContent('');
    setAnalyseResult('');
    setKeywords('');
    setCombinedKeywords('');
    setWebsites('');
    setInitialStuffedKeywords([]);
    setStuffedKeywords([]);
    setList({});
    setIsTextDelivered(false);
  }

  const handleInputChange = (e) => {
    setWebsites(e.target.value);
    checkIfAllLinesGreen(actualHtmlContent, e.target.value);
  };

  const checkIfAllLinesGreen = (isGreen) => {
    setAllWebsitesArePresent(isGreen);
  };

  const highlightWordsInHtml = (htmlContent, wordsToHighlight) => {
    if (!wordsToHighlight || wordsToHighlight.length === 0) {
      return htmlContent;
    }

    let processedContent = he.decode(htmlContent);
    let filteredKeywords = wordsToHighlight.filter((keyword) => {
      return keyword.length > 0 && keyword !== '';
    });

    return replaceTextInBold(processedContent, filteredKeywords);
  };

  const applyHighlightingAndWait = () => {
    return new Promise((resolve) => {
      if (!editorInitialized || !editorRef.current) {
        resolve(actualHtmlContent);
        return;
      }

      const editor = editorRef.current;
      const currentRawHtml = editor.getContent();
      const newHighlightedHtml = highlightWordsInHtml(
        currentRawHtml,
        wordsInBold
      );

      if (newHighlightedHtml !== currentRawHtml) {
        editor.setContent(newHighlightedHtml);
        setActualHtmlContent(() => {
          resolve(newHighlightedHtml);
          return newHighlightedHtml;
        });
        setActualTextContent(editor.getContent({ format: 'text' }));
      } else {
        resolve(actualHtmlContent);
      }
    });
  };

  useEffect(() => {
    document.title = t('Typix - Détails du texte AdminClient');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);



  useEffect(() => {
    enableSave();
  }, [redactionData, url, initialTextContent, enableSave]);

  useEffect(() => {
    checkValidForm();
  }, [checkValidForm]);

  useEffect(() => {
    enableSendRevision();
  }, [enableSendRevision, redactionData]);

  useEffect(() => {
    enableDraft();
  }, [enableDraft]);

  useEffect(() => {
    if (redactionData?.orderId && (!order || !order.id)) {
      dispatch(fetchAdminClientTextOrderDisplay(redactionData.orderId));
    }
  }, [dispatch, redactionData?.orderId, order]);

  useEffect(() => {
    return () => {
      if (order?.id) {
        dispatch(fetchAdminClientTextOrderDisplay(order.id));
      }
    };
  }, [dispatch, order?.id]);

  useEffect(() => {
    return () => {
      localStorage.removeItem('AdminClient_TextId');
      try {
        window.localStorage.removeItem('WriterClient_PendingTextType');
      } catch { }
    };
  }, []);

  if (isFetchLoading || isLoading || isError) return <Loader />;

  if (!redactionData) {
    return (
      <div className="content-center">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>❌ Aucune rédaction trouvée</h3>
          <p>
            currentRedaction est null/undefined et aucune donnée dans
            localStorage
          </p>
          <p>Redirection vers la page des projets...</p>
          <button
            onClick={() => history.push('/AdminClient/DetailsProject')}
            className="btn btn-primary"
          >
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-center">
      <Form
        ref={formRef}
        onSubmit={validateData}
        style={{
          transition: 'margin-right 0.3s ease',
          marginRight: sidebarCollapsed || !shouldDisplaySidebar ? 0 : sidebarWidth,
        }}
      >
        <FormHeader
          textTitle={
            redactionData?.title || textTitle || t('Nouveau texte AdminClient')
          }
          onBackClick={() => history.goBack()}
          onDownloadClick={() =>
            redactionData ? DownloadOneTextWordType(redactionData.id, user) : {}
          }
          userRole={ROLES.ADMINCLIENT}
        />

        <TextDetailsSection
          t={t}
          textId={redactionData?.id}
          expandMetaField
          formData={{
            textTitle,
            keyword,
            url,
            titleTag,
            metaDescriptionTag,
          }}
          handlers={{
            setTextTitle,
            setKeyword,
            setUrl,
            setTitleTag,
            setMetaDescriptionTag,
          }}
        />


        {(redactionData?.globalRevisionSent || redactionData?.revisionSent) &&
          redactionData?.revisionText && (
            <RevisionDetails
              t={t}
              textId={redactionData.id}
              revisionRequestAt={redactionData?.revisionRequestAt}
              revisionText={redactionData?.revisionText}
              revisionFileName={redactionData?.revisionFileName}
            />
          )}


        {(() => {
          const hasUrl = (url && url.trim() !== '') || (redactionData?.url && redactionData.url.trim() !== '');
          // Vérifier le type de projet depuis order ou utiliser une logique par défaut pour PREMIUM
          const isPremiumProject = order?.type === 'PREMIUM' || (!order && hasUrl);
          const shouldShow =
            hasInitialSection ||
            (isPremiumProject && hasUrl);
          console.log('🔍 Debug InitialTextSection display:', {
            hasInitialSection,
            orderType: order?.type,
            url,
            redactionDataUrl: redactionData?.url,
            urlTrimmed: url?.trim(),
            hasUrl,
            isPremiumProject,
            shouldShow,
          });
          return shouldShow;
        })() && (
            <div className="row mt-2">
              <InitialTextSection
                isTextFieldDisabled={isTextFieldDisabled}
                formData={redactionData}
                textType={uiTextType}
                url={url}
                initialHtmlContent={initialHtmlContent}
                handleStepOneEditorInit={handleStepOneEditorInit}
                setInitialHtmlContent={setInitialHtmlContent}
                setInitialTextContent={setInitialTextContent}
                setInitialTextWordCount={setInitialTextWordCount}
                userRole={user.role}
                t={t}
                isNotTranslation={isEditorial}
                isSaveDisabled={isSaveDisabled}
                isTextSaved={isTextSaved}
              />
            </div>
          )}

        {(() => {
          const hasUrl = (url && url.trim() !== '') || (redactionData?.url && redactionData.url.trim() !== '');
          const isPremiumProject = order?.type === 'PREMIUM' || (!order && hasUrl);
          const shouldShow = hasInitialSection || (isPremiumProject && hasUrl);
          const hasContentInSection2 =
            (actualTextContent && actualTextContent.trim().length > 0) ||
            (actualHtmlContent && actualHtmlContent.trim().length > 0);

          // Enregistrer : afficher uniquement quand l'utilisateur écrit dans la section 1 (éditeur 1)
          // Si l'utilisateur a écrit dans la section 2, cacher Enregistrer
          if (!shouldShow || isTextSaved || hasContentInSection2) return null;

          const handleInitialSave = async (event) => {
            event.preventDefault();
            try {
              const htmlFromEditor =
                stepOneEditorRef.current?.getContent?.() || '';
              const textFromEditor =
                stepOneEditorRef.current?.getContent?.({
                  format: 'text',
                }) || '';

              const htmlToCopy = htmlFromEditor || initialHtmlContent || '';
              const textToCopy = textFromEditor || initialTextContent || '';

              setActualHtmlContent(htmlToCopy);
              setActualTextContent(textToCopy);

              const savedData = await handleSave('manualDraft', {
                actualHtmlContent: htmlToCopy,
                actualTextContent: textToCopy,
              });

              setHasPerformedInitialSave(true);

              // Marquer qu'il faut recharger les données après la fermeture du popup
              const textIdToReload = savedData?.id || redactionData?.id;
              if (textIdToReload) {
                setSavedTextId(textIdToReload);
                setShouldReloadAfterSave(true);
              }

              if (editorRef.current && editorInitialized) {
                const contentToSet = htmlToCopy || textToCopy || '';
                editorRef.current.setContent(contentToSet);
              }
            } catch (error) {
              console.error('❌ Erreur lors de la sauvegarde initiale :', error);
            }
          };

          return (
            <div className="row mt-2">
              <div className="col-12 d-flex justify-content-end">
                <button
                  className={
                    isSaveDisabled ? 'bt-submit-disabled-y' : 'bt-submit-y'
                  }
                  type="button"
                  onClick={handleInitialSave}
                  disabled={isSaveDisabled}
                >
                  {t('Enregistrer')}
                </button>
              </div>
            </div>
          );
        })()}

        <div className="row mt-2">
          <div className="col-12">
            <div>
              <div className="label-title label-text-h">
                {t('Ajouter ou compléter ci-dessous votre contenu')} :
              </div>

              <div className="box-wrapper-editor">
                <CustomEditor
                  disabled={false}
                  textId={redactionData?.id}
                  content={
                    (actualHtmlContent && actualHtmlContent.length > 0)
                      ? actualHtmlContent
                      : actualTextContent
                  }
                  onInit={(evt, editor) => handleEditorInit(editor)}
                  onChange={(value, editor) => {
                    let textValue = editor.getContent({ format: 'text' });
                    const newWordCount = editor.plugins.wordcount.getCount();
                    debouncedSetActualTextWordCount(newWordCount);


                    scheduleSave(
                      textValue,
                      value,
                      currentRedaction,
                      initialHtmlContent,
                      initialTextContent,
                      'SMART',
                      url,
                      titleTag,
                      metaDescriptionTag,
                      currentContentScore ||
                      redactionData?.contentScore ||
                      redactionData?.initialScoreData ||
                      null
                    );


                    setActualHtmlContent(value);
                    setActualTextContent(value);
                    setAnalyseText(textValue);

                    checkIfAllLinesGreen(value, websites);
                  }}
                  t={t}
                  orderType="SMART"
                />
              </div>
            </div>

            <SavingIndicator isSaving={isSaving} />

            <div className="mt-2 mb-2 text-end" style={{ fontSize: '15px', color: '#666', fontWeight: '500' }}>
              {t('Nombre total de mots ajoutés')} :{' '}
              <strong style={{ color: '#000' }}>
                {Math.max(0, (actualTextWordCount || 0) - computedInitialWordCount)}
              </strong>
            </div>

            <div className="d-flex justify-content-between flex-wrap mt-3">


              <div>

                <div className="d-flex">


                  {!redactionData?.id || !isTextSaved ? (
                    (() => {
                      const hasUrl =
                        (url && url.trim() !== '') ||
                        (redactionData?.url && redactionData.url.trim() !== '');
                      const isPremiumProject =
                        order?.type === 'PREMIUM' || (!order && hasUrl);
                      const showInitialSection =
                        hasInitialSection || (isPremiumProject && hasUrl);
                      const hasContentInSection2 =
                        (actualTextContent && actualTextContent.trim().length > 0) ||
                        (actualHtmlContent && actualHtmlContent.trim().length > 0);

                      // Brouillon sous éditeur 2 : afficher quand l'utilisateur écrit dans la section 2
                      // ou quand la section 1 n'est pas affichée
                      if (showInitialSection && !hasContentInSection2) {
                        return null;
                      }

                      return (
                        <button
                          type="button"
                          className="btn"
                          onClick={async () => {
                            const finalHtml = await applyHighlightingAndWait();
                            const savedData = await handleSave('manualDraft');
                            setHasPerformedInitialSave(true);

                            // Marquer qu'il faut recharger les données après la fermeture du popup
                            const textIdToReload = savedData?.id || redactionData?.id;
                            if (textIdToReload) {
                              setSavedTextId(textIdToReload);
                              setShouldReloadAfterSave(true);
                            }
                          }}
                          disabled={
                            !actualTextContent ||
                            actualTextContent.trim().length === 0
                          }
                          style={{
                            background:
                              !actualTextContent ||
                                actualTextContent.trim().length === 0
                                ? '#f5f5f5'
                                : '#6458F1',
                            border: 'none',
                            color:
                              !actualTextContent ||
                                actualTextContent.trim().length === 0
                                ? '#999'
                                : 'white',
                            borderRadius: '6px',
                            padding: '12px 18px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                            textTransform: 'none',
                            cursor:
                              !actualTextContent ||
                                actualTextContent.trim().length === 0
                                ? 'not-allowed'
                                : 'pointer',
                            opacity:
                              !actualTextContent ||
                                actualTextContent.trim().length === 0
                                ? 0.6
                                : 1,
                          }}
                        >
                          {t('Brouillon')}
                        </button>
                      );
                    })()
                  ) : !redactionData?.delivered ? (
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setActionsOpen(!actionsOpen)}
                        style={{
                          background: '#6458F1',
                          border: 'none',
                          color: '#ffffff',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                          textTransform: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {t('Actions')} | <i className={`fa fa-chevron-${actionsOpen ? 'down' : 'up'}`} />
                      </button>
                      {actionsOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: '100%',
                            marginBottom: 8,
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow:
                              '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
                          }}
                        >
                          {/* Actions pour texte en brouillon : Brouillon, Livrer */}
                          <div
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #f0f0f0',
                              borderTopLeftRadius: 12,
                              borderTopRightRadius: 12,
                              background: '#F5F3FF',
                              color: '#6458F1',
                              cursor: 'pointer'
                            }}
                            onClick={async () => {
                              setActionsOpen(false);
                              const finalHtml = await applyHighlightingAndWait();
                              await handleSave('manualDraft');
                            }}
                          >
                            {t('Brouillon')}
                          </div>
                          <div
                            style={{ padding: '12px 16px', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, cursor: 'pointer', background: '#F5F3FF', color: '#6458F1' }}
                            onClick={async () => {
                              setActionsOpen(false);
                              const finalHtml = await applyHighlightingAndWait();
                              await deliverText('manualDeliver', finalHtml);
                              if (redactionData?.id) {
                                await reloadRedactionData(redactionData.id);
                              }
                            }}
                          >
                            {t('Livrer')}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      {shouldDisplaySidebar && (
        <TextDisplaySidebar
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          width={sidebarWidth}
          setWidth={setSidebarWidth}
          combinedKeywords={combinedKeywords}
          autoKeywordsGenerationLoading={autoKeywordsGenerationLoading}
          keywords={keywords}
          textType={uiTextType}
          orderContentType={uiTextType}
          websites={websites}
          actualHtmlContent={actualHtmlContent}
          actualTextContent={actualTextContent}
          initialTextContent={initialTextContent}
          isDelivered={redactionData?.delivered}
          userRole={user.role}
          orderWebsite={
            (order?.website?.urlSite || redactionData?.orderWebsite || '')
          }
          country={order?.website?.country}
          t={t}
          keywordStuffingLoading={keywordStuffingLoading}
          keyword={keyword}
          list={list}
          language={projectLanguage}
          maxWords={redactionData?.maxWords || order?.maxWords || 1000}
          wordCount={actualTextWordCount}
          initialContentScore={
            currentContentScore ||
            redactionData?.contentScore ||
            redactionData?.initialScoreData ||
            null
          }
          textId={redactionData?.id || null}
          preCalculatedScoreData={
            currentContentScore ||
            redactionData?.contentScore ||
            redactionData?.initialScoreData ||
            null
          }
          onScoreDataChange={handleScoreDataChange}
          handleInputChange={handleInputChange}
          handleKeywordChange={handleKeywordChange}
          handleGapUpdate={handleGapUpdate}
          checkIfAllLinesGreen={checkIfAllLinesGreen}
          mode="adminClient"
          defaultScoreTab="seo"
          onGenerateKeywords={() => {
            keywordsAutoGeneration(
              currentRedaction?.orderId || '',
              keyword,
              '',
              url,
              setKeywords,
              setCombinedKeywords,
              setCombinedKeywordsList,
              setWebsites,
              setErrorSuccessMsg,
              setIsSuccess,
              setErrorSuccess,
              t,
              setAutokeywordGenerationLoading
            );
          }}
        />
      )}

      <ComparisonModal
        open={showComparison}
        initialText={initialTextContent}
        text={he.decode(actualTextContent)}
        handleClose={() => setShowComparison(false)}
      />

      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => setConfirmation(false)}
        executeAction={handleSave}
      />

      <KeywordStuffingModal
        open={showKeywordStuffingModal}
        title={keywordStuffingModalTitle}
        message={keywordStuffingModalMsg}
        stuffedKeywords={keywordStuffingModalData}
        showButtonClose={keywordStuffingModalCloseBtn}
        handleClose={() => setShowKeywordStuffingModal(false)}
        executeAction={() => putToDraft('keywordStuffingDraft')}
      />

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={async () => {
          setErrorSuccess(false);

          // Recharger les données après la fermeture du popup si nécessaire
          if (shouldReloadAfterSave && savedTextId) {
            await reloadRedactionData(savedTextId);
            setShouldReloadAfterSave(false);
            setSavedTextId(null);
          }

          if (
            errorSuccessMsg ===
            KeywordStuffingModalPrompts.demandeRevisionKeywordStuffing
          ) {
            history.goBack();
          }
        }}
        success={isSuccess}
        isTextDelivered={isTextDelivered}
        resetFields={resetFields}
        redactionId={redactionData?.id}
        showCreditButton={false}
        executeAction={() => { }}
        toggle={() => setErrorSuccess(!errorSuccess)}
        user={user}
        textType={uiTextType}
        imageUrl={errorSuccessImg}
      />
    </div>
  );
}

function WriterClientTextDisplay() {
  usePageTracking(WRITER_CLIENT_TEXT_DETAILS_PAGE);

  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <WriterClientTextDisplayContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default WriterClientTextDisplay;
